import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Stack,
  Title,
  Text,
  Button,
  Paper,
  Group,
  Alert,
  Collapse,
  ActionIcon,
  Flex,
  Card,
  Badge,
  Menu,
  Pagination,
  Box,
  rem,
  SimpleGrid,
  Skeleton,
  Select,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useFeatureFlag } from '../../hooks/useFeatureFlags';
import {
  IconBook,
  IconClock,
  IconUsers,
  IconChevronLeft,
  IconDotsVertical,
  IconEdit,
  IconAlertCircle,
  IconBookmark,
  IconTrash,
  IconArchive,
  IconArrowUp,
  IconArrowDown
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../hooks/useAuth';
import { RecipeProvenanceBadges } from '../Recipe/RecipeProvenanceBadges';
import { useAppUser } from '../../hooks/useAppUser';
import { Recipe } from '../../types/mealPlan';
import { MobileOptimized } from '../Layout/MobileOptimized';
import { formatTime } from '../../utils/formatTime';
import { RecipeSearchFilters } from '../Recipe/RecipeSearchFilters';
import { MEAL_SLOT_MATCHERS } from '../../utils/mealSlotMatchers';
import { RecipeImage } from '../UI/RecipeImage';
import { TopPickBadge } from '../UI/TopPickRibbon';
import { saveArchiveState, loadArchiveState } from '../../utils/persistentState';
import { getPageCache, setPageCache } from '../../utils/pageCache';
import { fetchRecipeList, recipeListCacheKey } from '../../utils/recipeListCache';
import { buildRecipePath } from '../../utils/routeHelpers';
import { getRecipeBadges, splitBadgesForDisplay, RecipeBadgeViewer } from '../../utils/recipeBadges';
import { MoreRestrictionsMenu } from '../UI/MoreRestrictionsMenu';
import { householdSegment } from '../../utils/householdSegment';
import { CuisineBadge } from '../UI/CuisineBadge';
import { formatCount } from '../../utils/formatCount';

interface RecipeArchiveProps {
  onBack: () => void;
}

export const RecipeArchive = ({ onBack }: RecipeArchiveProps) => {
  const { user } = useAuth();
  const { user: appUser } = useAppUser();
  const effectiveUid = appUser?.uid || user?.uid;
  const badgeViewer: RecipeBadgeViewer = {
    dietaryRestrictions: appUser?.familyProfile?.dietaryRestrictions,
    householdSegment: householdSegment(appUser?.familyProfile),
  };
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  // Seed from the archive this session last displayed, so a back navigation
  // renders recipes on the first paint and the mount fetch refreshes silently.
  // Shared with the swap drawer's archive tab (utils/recipeListCache.ts).
  const archiveCacheKey = recipeListCacheKey('archive', effectiveUid);
  const seededArchive = getPageCache<Recipe[]>(archiveCacheKey);
  const [recipes, setRecipes] = useState<Recipe[]>(seededArchive ?? []);
  // Skeletons until the first fetch when there's no seed — starting false
  // painted the "no archived recipes" empty state before the fetch began
  const [loading, setLoading] = useState(!seededArchive);
  const [error, setError] = useState<string | null>(null);
  const hasArchiveDataRef = useRef(!!seededArchive);

  // Initialize state from localStorage or defaults
  const initializeState = () => {
    const savedState = loadArchiveState();
    return {
      searchQuery: savedState?.searchQuery ?? '',
      selectedCuisines: savedState?.selectedCuisines ?? [],
      selectedDifficulty: savedState?.selectedDifficulty ?? '',
      selectedDietaryRestrictions: savedState?.selectedDietaryRestrictions ?? [],
      selectedPrepTime: savedState?.selectedPrepTime ?? '',
      selectedTotalTime: savedState?.selectedTotalTime ?? '',
      selectedOrigin: savedState?.selectedOrigin ?? '',
      selectedMeal: savedState?.selectedMeal ?? '',
      sortField: savedState?.sortField ?? 'name',
      sortDirection: savedState?.sortDirection ?? 'asc' as 'asc' | 'desc',
      currentPage: savedState?.currentPage ?? 1,
    };
  };

  const initialState = initializeState();
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState(initialState.searchQuery);
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>(initialState.selectedCuisines);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>(initialState.selectedDifficulty);
  const [selectedDietaryRestrictions, setSelectedDietaryRestrictions] = useState<string[]>(initialState.selectedDietaryRestrictions);
  const [selectedPrepTime, setSelectedPrepTime] = useState<string>(initialState.selectedPrepTime);
  const [selectedTotalTime, setSelectedTotalTime] = useState<string>(initialState.selectedTotalTime);
  const [selectedOrigin, setSelectedOrigin] = useState<string>(initialState.selectedOrigin);
  const [selectedMeal, setSelectedMeal] = useState<string>(initialState.selectedMeal);
  // The Meal filter only exists behind the slots flag; without it the
  // handler stays unset and RecipeSearchFilters hides the control
  const additionalMealsEnabled = useFeatureFlag('additional_meals');
  
  // Sorting state
  const [sortField, setSortField] = useState<string>(initialState.sortField);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialState.sortDirection);
  
  // Track recipes that have been optimistically removed
  const [optimisticallyRemovedRecipes, setOptimisticallyRemovedRecipes] = useState<Set<string>>(new Set());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const recipesPerPage = 12;
  const firstRecipeRef = useRef<HTMLDivElement>(null);
  const hasInitiallyLoadedRef = useRef(false);

  // Suppress ResizeObserver errors - this is a common, harmless browser issue
  // Reference: https://trackjs.com/javascript-errors/resizeobserver-loop-completed-with-undelivered-notifications/
  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
        const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    };
    
    window.addEventListener('error', handleError);
    
    return () => window.removeEventListener('error', handleError);
  }, []);

  const fetchArchivedRecipes = useCallback(async (showLoading: boolean = true) => {
    if (!user) return;
    
    if (showLoading) {
      setLoading(true);
      setError(null);
    }
    
    try {
      const fresh = await fetchRecipeList(user, 'archive');
      hasArchiveDataRef.current = true;
      setRecipes(fresh);
    } catch (error: any) {
      console.error('Error fetching archived recipes:', error);
      if (showLoading) {
        setError(error.message);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [user]);

  const addRecipeToCookbook = useCallback(async (recipeId: string) => {
    if (!user) return false;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/cookbook/${recipeId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to add recipe to cookbook');
      }

      return true;
    } catch (error: any) {
      console.error('Error adding recipe to cookbook:', error);
      setError(error.message);
      return false;
    }
  }, [user]);

  const deleteRecipe = useCallback(async (recipeId: string) => {
    if (!user) return false;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/id/${recipeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete recipe');
      }

      return true;
    } catch (error: any) {
      console.error('Error deleting recipe:', error);
      setError(error.message);
      return false;
    }
  }, [user]);

  const undoDeleteRecipe = useCallback(async (recipeId: string) => {
    if (!user) return false;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/id/${recipeId}/undo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to restore recipe');
      }
      
      return true;
    } catch (error: any) {
      console.error('Error restoring recipe:', error);
      setError(error.message);
      return false;
    }
  }, [user]);

  useEffect(() => {
    // With seeded content this is a silent background refresh.
    fetchArchivedRecipes(!hasArchiveDataRef.current);
  }, [fetchArchivedRecipes]);

  // Mirror displayed data into the session cache so local mutations (restore,
  // delete) are what a back navigation resumes from.
  useEffect(() => {
    if (hasArchiveDataRef.current) {
      setPageCache(archiveCacheKey, recipes);
    }
  }, [recipes, archiveCacheKey]);

  // Get unique values for filter options
  const cuisineOptions = useMemo(() => {
    const cuisines = new Set(recipes.map(r => r.cuisine).filter(Boolean) as string[]);
    return Array.from(cuisines).sort();
  }, [recipes]);

  const dietaryOptions = useMemo(() => {
    const restrictions = new Set<string>();
    recipes.forEach(recipe => {
      recipe.dietaryRestrictions?.forEach(restriction => restrictions.add(restriction));
    });
    return Array.from(restrictions).sort();
  }, [recipes]);

  // Filter and sort recipes
  const filteredRecipes = useMemo(() => {
    const filtered = recipes.filter(recipe => {
      // Exclude optimistically removed recipes
      if (optimisticallyRemovedRecipes.has(recipe.id)) return false;
      
      // Search filter - search by name, description, and cuisine
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = recipe.name.toLowerCase().includes(query) ||
          recipe.description?.toLowerCase().includes(query) ||
          recipe.cuisine?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Cuisine filter
      if (selectedCuisines.length > 0) {
        if (!recipe.cuisine || !selectedCuisines.includes(recipe.cuisine)) {
          return false;
        }
      }

      // Difficulty filter
      if (selectedDifficulty && recipe.difficulty !== selectedDifficulty) {
        return false;
      }

      // Prep time filter
      if (selectedPrepTime) {
        const prepTime = recipe.prepTimeMinutes;
        switch (selectedPrepTime) {
          case 'quick':
            if (prepTime > 15) return false;
            break;
          case 'medium':
            if (prepTime <= 15 || prepTime > 30) return false;
            break;
          case 'long':
            if (prepTime <= 30) return false;
            break;
        }
      }

      // Total time filter
      if (selectedTotalTime) {
        const totalTime = recipe.totalTimeMinutes;
        switch (selectedTotalTime) {
          case 'quick':
            if (totalTime > 30) return false;
            break;
          case 'medium':
            if (totalTime <= 30 || totalTime > 60) return false;
            break;
          case 'long':
            if (totalTime <= 60) return false;
            break;
        }
      }

      // Dietary restrictions filter
      if (selectedDietaryRestrictions.length > 0) {
        const recipeRestrictions = recipe.dietaryRestrictions || [];
        const hasAllRestrictions = selectedDietaryRestrictions.every(restriction =>
          recipeRestrictions.includes(restriction)
        );
        if (!hasAllRestrictions) return false;
      }

      // Origin filter
      if (selectedOrigin && recipe.origin !== selectedOrigin) {
        return false;
      }

      // Meal filter: slots-first with course fallback (untagged reads as
      // dinner-ish, same as everywhere else). Flag-guarded so a persisted
      // selection can't invisibly hide recipes once the control is gone
      if (
        additionalMealsEnabled &&
        selectedMeal &&
        !MEAL_SLOT_MATCHERS[selectedMeal as keyof typeof MEAL_SLOT_MATCHERS]?.(recipe)
      ) {
        return false;
      }

      return true;
    });

    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'created':
          aValue = new Date(a.createdAt || 0);
          bValue = new Date(b.createdAt || 0);
          break;
        case 'totalTime':
          aValue = a.totalTimeMinutes;
          bValue = b.totalTimeMinutes;
          break;
        case 'difficulty':
          // Sort by difficulty: easy < medium < hard
          const difficultyOrder = { 'easy': 1, 'medium': 2, 'hard': 3 };
          aValue = difficultyOrder[a.difficulty || 'medium'];
          bValue = difficultyOrder[b.difficulty || 'medium'];
          break;
        case 'usage':
          aValue = a.usageCount || 0;
          bValue = b.usageCount || 0;
          break;
        case 'cuisine':
          aValue = (a.cuisine || '').toLowerCase();
          bValue = (b.cuisine || '').toLowerCase();
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [recipes, searchQuery, selectedCuisines, selectedDifficulty, selectedPrepTime, selectedTotalTime, selectedDietaryRestrictions, selectedOrigin, selectedMeal, additionalMealsEnabled, optimisticallyRemovedRecipes, sortField, sortDirection]);

  const handleUndo = async (recipe: Recipe, notificationId: string) => {
    // Restore deleted recipe
    const success = await undoDeleteRecipe(recipe.id);
    if (success) {
      // Add recipe back to the recipes array
      setRecipes(prev => {
        // Check if recipe already exists to avoid duplicates
        const exists = prev.some(r => r.id === recipe.id);
        return exists ? prev : [...prev, recipe];
      });
      
      // Remove from optimistically removed set so recipe reappears
      setOptimisticallyRemovedRecipes(prev => {
        const newSet = new Set(prev);
        newSet.delete(recipe.id);
        return newSet;
      });
      notifications.hide(notificationId);
    } else {
      notifications.show({
        title: 'Error',
        message: 'Failed to restore recipe',
        color: 'red',
      });
    }
  };


  const handleRecipeAction = async (recipe: Recipe, action: 'save' | 'delete') => {
    let success = false;
    
    if (action === 'save') {
      success = await addRecipeToCookbook(recipe.id);
      if (success) {
        // Remove from archive list immediately
        setRecipes(prev => prev.filter(r => r.id !== recipe.id));
        notifications.show({
          title: 'Recipe added to cookbook',
          message: `"${recipe.name}" has been added to your cookbook`,
          color: 'green',
        });
      }
    } else if (action === 'delete') {
      // Optimistically remove recipe from UI immediately
      setOptimisticallyRemovedRecipes(prev => new Set(prev).add(recipe.id));
      
      const notificationId = `delete-${recipe.id}`;
      
      // Create undo button component with loading state
      const UndoButton = () => {
        const [isUndoing, setIsUndoing] = useState(false);
        
        return (
          <Button
            size="xs"
            variant="subtle"
            color="orange"
            loading={isUndoing}
            onClick={async () => {
              setIsUndoing(true);
              try {
                await handleUndo(recipe, notificationId);
              } finally {
                setIsUndoing(false);
              }
            }}
          >
            Undo
          </Button>
        );
      };
      
      // Show notification with undo option immediately
      notifications.show({
        id: notificationId,
        title: `Deleted "${recipe.name}"`,
        message: (
          <Group>
            <Text size="sm">Recipe has been deleted</Text>
            <UndoButton />
          </Group>
        ),
        color: 'orange',
        autoClose: 5000,
        withCloseButton: true,
      });
      
      // Perform actual API call in background
      try {
        success = await deleteRecipe(recipe.id);
        if (success) {
          // Remove from actual recipes state
          setRecipes(prev => prev.filter(r => r.id !== recipe.id));
        } else {
          // If API call failed, restore the recipe and show error
          setOptimisticallyRemovedRecipes(prev => {
            const newSet = new Set(prev);
            newSet.delete(recipe.id);
            return newSet;
          });
          notifications.hide(notificationId);
          notifications.show({
            title: 'Error',
            message: 'Failed to delete recipe. Please try again.',
            color: 'red',
          });
        }
      } catch (error) {
        // If API call errored, restore the recipe and show error
        setOptimisticallyRemovedRecipes(prev => {
          const newSet = new Set(prev);
          newSet.delete(recipe.id);
          return newSet;
        });
        notifications.hide(notificationId);
        notifications.show({
          title: 'Error',
          message: 'Failed to delete recipe. Please try again.',
          color: 'red',
        });
      }
    }
    
    if (action === 'save' && !success) {
      notifications.show({
        title: 'Error',
        message: 'Failed to add recipe to cookbook. Please try again.',
        color: 'red',
      });
    }
  };

  const handleSaveClick = (recipe: Recipe) => {
    handleRecipeAction(recipe, 'save');
  };

  const handleDeleteClick = (recipe: Recipe) => {
    handleRecipeAction(recipe, 'delete');
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCuisines([]);
    setSelectedDifficulty('');
    setSelectedDietaryRestrictions([]);
    setSelectedPrepTime('');
    setSelectedTotalTime('');
    setSelectedOrigin('');
    setSelectedMeal('');
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredRecipes.length / recipesPerPage);
  const startIndex = (currentPage - 1) * recipesPerPage;
  const endIndex = startIndex + recipesPerPage;
  const currentRecipes = filteredRecipes.slice(startIndex, endIndex);

  // Scroll to top when page changes (but not on initial load)
  useEffect(() => {
    if (firstRecipeRef.current && currentRecipes.length > 0 && hasInitiallyLoadedRef.current) {
      // Use requestAnimationFrame for better timing with DOM updates
      requestAnimationFrame(() => {
        firstRecipeRef.current?.scrollIntoView({ 
          behavior: 'auto', 
          block: 'center' // Use center to avoid being cut off by the navbar
        });
      });
    }
    // Mark as initially loaded after first render
    if (currentRecipes.length > 0 && !hasInitiallyLoadedRef.current) {
      hasInitiallyLoadedRef.current = true;
    }
  }, [currentPage, currentRecipes.length]);

  // Save state to localStorage whenever it changes (including pagination)
  useEffect(() => {
    const currentState = {
      searchQuery,
      selectedCuisines,
      selectedDifficulty,
      selectedDietaryRestrictions,
      selectedPrepTime,
      selectedTotalTime,
      selectedOrigin,
      selectedMeal,
      sortField,
      sortDirection,
      currentPage,
    };
    saveArchiveState(currentState);
  }, [searchQuery, selectedCuisines, selectedDifficulty, selectedDietaryRestrictions, selectedPrepTime, selectedTotalTime, selectedOrigin, selectedMeal, sortField, sortDirection, currentPage]);

  // Auto-reset to page 1 when filters change and current page would be invalid
  useEffect(() => {
    const totalPages = Math.ceil(filteredRecipes.length / recipesPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredRecipes.length, currentPage, recipesPerPage]);

  const handleRecipeClick = (recipe: Recipe) => {
    if (!user) return;
    navigate(buildRecipePath(recipe, effectiveUid!), { state: { recipe } });
  };

  return (
    <MobileOptimized>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ActionIcon 
              variant="subtle" 
              onClick={onBack}
              size="lg"
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <Stack gap={0} className="page-header">
              <Title order={2}>Recipe archive</Title>
              {/* div (not p) so the loading skeleton can sit inside, keeping
                  the subtitle's line height instead of flashing "0 archived" */}
              <Text component="div" c="dimmed" size="sm" className="page-subtitle">
                {loading ? (
                  <Skeleton height={12} width={140} display="inline-block" style={{ verticalAlign: 'middle' }} />
                ) : (
                  <>{formatCount(recipes.length)} archived recipe{recipes.length !== 1 ? 's' : ''}</>
                )}
              </Text>
            </Stack>
          </Group>
          
          {/* Desktop: My cookbook button */}
          <Group visibleFrom="sm">
            <Button
              variant="subtle"
              leftSection={<IconBook size={16} />}
              onClick={onBack}
            >
              My cookbook
            </Button>
          </Group>
          
          {/* Mobile: Dots menu */}
          <Group hiddenFrom="sm">
            <Menu transitionProps={{ transition: 'pop' }} position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="orange"
                  size="lg"
                >
                  <IconDotsVertical size={20} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconBook size={18} stroke={1.5} />}
                  onClick={onBack}
                  style={{ fontSize: '16px', padding: '12px 16px' }}
                >
                  My cookbook
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>

        <Collapse in={!!error}>
          <Alert icon={<IconAlertCircle size={16} color="red" />} color="red" radius="lg">
            <Text size="sm" c="red.7">{error}</Text>
          </Alert>
        </Collapse>

        {/* Search and Filters */}
        <Paper p="md" radius="lg" withBorder>
          <RecipeSearchFilters
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            selectedCuisines={selectedCuisines}
            selectedDifficulty={selectedDifficulty}
            selectedDietaryRestrictions={selectedDietaryRestrictions}
            selectedPrepTime={selectedPrepTime}
            selectedTotalTime={selectedTotalTime}
            selectedOrigin={selectedOrigin}
            showNeedsReviewOnly={false}
            cuisineOptions={cuisineOptions}
            dietaryOptions={dietaryOptions}
            onSelectedCuisinesChange={setSelectedCuisines}
            onSelectedDifficultyChange={setSelectedDifficulty}
            onSelectedDietaryRestrictionsChange={setSelectedDietaryRestrictions}
            onSelectedPrepTimeChange={setSelectedPrepTime}
            onSelectedTotalTimeChange={setSelectedTotalTime}
            onSelectedOriginChange={setSelectedOrigin}
            selectedMeal={selectedMeal}
            onSelectedMealChange={additionalMealsEnabled ? setSelectedMeal : undefined}
            onShowNeedsReviewOnlyChange={() => {}} // Not used in archive
            onClearFilters={clearFilters}
            showCollapsible={true}
            showNeedsReviewFilter={false}
            showClearAllButton={true}
            defaultExpanded={false}
            searchPlaceholder="Search archived recipes by name, description, or cuisine..."
          />
        </Paper>

        {/* Sort Options */}
        <Group justify="space-between" align="center">
          {loading ? (
            <Skeleton height={20} width="min(280px, 85%)" />
          ) : (
            <Text fw={500}>
              {formatCount(filteredRecipes.length)} recipe{filteredRecipes.length !== 1 ? 's' : ''} found
              {filteredRecipes.length > recipesPerPage && (
                <Text component="span" c="dimmed" size="sm">
                  {' '}• Showing {formatCount(startIndex + 1)}-{formatCount(Math.min(endIndex, filteredRecipes.length))} of {formatCount(filteredRecipes.length)}
                </Text>
              )}
            </Text>
          )}
          {loading ? (
            <Group gap="xs">
              <Skeleton height={36} width={140} radius="md" />
              <Skeleton height={28} width={28} radius="md" />
            </Group>
          ) : (
            <Group gap="xs">
              <Select
                placeholder="Sort by"
                value={sortField}
                onChange={(value) => setSortField(value || 'name')}
                data={[
                  { value: 'name', label: 'Name' },
                  { value: 'created', label: 'Date added' },
                  { value: 'totalTime', label: 'Total time' },
                  { value: 'difficulty', label: 'Difficulty' },
                  { value: 'usage', label: 'Usage count' },
                  { value: 'cuisine', label: 'Cuisine' }
                ]}
                size="sm"
                w={140}
              />
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                title={`Sort ${sortDirection === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                {sortDirection === 'asc' ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />}
              </ActionIcon>
            </Group>
          )}
        </Group>

        {/* Results */}
        <Stack gap="md">

          {loading ? (
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} p="md" radius="lg" withBorder>
                  <Flex gap={{ base: 'sm', sm: 'md' }}>
                    <Skeleton w={{ base: 80, sm: 120 }} h={{ base: 80, sm: 120 }} radius="md" style={{ flexShrink: 0 }} />
                    <Stack gap="xs" style={{ flex: 1 }}>
                      <Skeleton height={20} width="70%" />
                      <Skeleton height={14} width="100%" />
                      <Skeleton height={14} width="80%" />
                      {/* Time + servings + difficulty share a line on tablet+ */}
                      <Group gap="xs">
                        <Skeleton height={14} width={80} />
                        <Skeleton height={14} width={80} />
                        <Skeleton height={18} width={50} radius="xl" />
                      </Group>
                      {/* Cuisine + dietary/tag badges */}
                      <Group gap="xs">
                        <Skeleton height={18} width={60} radius="xl" />
                        <Skeleton height={18} width={70} radius="xl" />
                        <Skeleton height={18} width={80} radius="xl" />
                      </Group>
                    </Stack>
                  </Flex>
                </Card>
              ))}
            </SimpleGrid>
          ) : filteredRecipes.length === 0 ? (
            <Paper p="xl" radius="lg" withBorder ta="center">
              <Stack gap="md" align="center">
                <IconArchive size={48} color="var(--mantine-color-gray-5)" />
                <Stack gap="xs" align="center">
                  <Text fw={500}>No archived recipes found</Text>
                  <Text c="dimmed" size="sm">
                    {recipes.length === 0 
                      ? "You don't have any archived recipes yet."
                      : "Try adjusting your search or filters."
                    }
                  </Text>
                </Stack>
              </Stack>
            </Paper>
          ) : (
            <>
              <SimpleGrid 
                cols={{ base: 1, md: 2 }}
                spacing="md"
              >
                {currentRecipes.map((recipe, index) => (
                  <Card
                    key={recipe.id}
                    ref={index === 0 ? firstRecipeRef : undefined}
                    p="md"
                    radius="lg"
                    withBorder
                  >
                    <Flex gap={{ base: 'sm', sm: 'md' }}>
                      {/* Recipe Image */}
                      <Stack gap="xs" align="center">
                        <Box
                          w={{ base: 80, sm: 120 }}
                          h={{ base: 80, sm: 120 }}
                          style={{
                            flexShrink: 0,
                            borderRadius: 'var(--mantine-radius-md)',
                            overflow: 'hidden',
                            backgroundColor: 'var(--mantine-color-gray-1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleRecipeClick(recipe)}
                        >
                          <RecipeImage
                            src={recipe.imageUrl}
                            alt={recipe.name}
                            width="100%"
                            height="100%"
                            fallbackIconSize={32}
                          />
                          {/* Overlaid on the thumbnail; the provenance strip
                              below hides its Top pick chip (hideTopPick) so
                              the marker isn't doubled */}
                          {recipe.isCurated && recipe.origin !== 'user_recipe' && !recipe.isSaved && (
                            <Box
                              style={{
                                position: 'absolute',
                                bottom: 4,
                                left: 4,
                                pointerEvents: 'none',
                                zIndex: 1,
                              }}
                            >
                              <TopPickBadge />
                            </Box>
                          )}
                        </Box>
                      </Stack>
                      
                      {/* Recipe Content */}
                      <Stack gap="xs" style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                        <Group gap="xs" align="flex-start" style={{ paddingRight: 40 }}>
                          {/* md at base: beside the 80px thumbnail a ~120px
                              title column at lg wraps one word per line */}
                          <Text
                            fw={500}
                            fz={{ base: 'md', sm: 'lg' }}
                            style={{
                              cursor: 'pointer'
                            }}
                            onClick={() => handleRecipeClick(recipe)}
                          >
                            {recipe.name}
                          </Text>
                        </Group>
                        <Menu shadow="md" width={220}>
                          <Menu.Target>
                            <ActionIcon 
                              variant="subtle" 
                              size="lg" 
                              style={{ position: 'absolute', top: -4, right: -4 }}
                            >
                              <IconDotsVertical size={20} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}
                              onClick={() => {
                                if (user) {
                                  navigate(`${buildRecipePath(recipe, effectiveUid!)}?edit=true`, { state: { recipe } });
                                }
                              }}
                              style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                            >
                              Edit Recipe
                            </Menu.Item>
                            <Menu.Item 
                              leftSection={<IconBookmark style={{ width: rem(14), height: rem(14) }} />}
                              color="green"
                              onClick={() => handleSaveClick(recipe)}
                              style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                            >
                              Add to Cookbook
                            </Menu.Item>
                            <Menu.Item 
                              leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                              color="red"
                              onClick={() => handleDeleteClick(recipe)}
                              style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                            >
                              Delete recipe
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                        
                        {recipe.description && (
                          <Text size="sm" c="dimmed" lineClamp={2}>
                            {recipe.description}
                          </Text>
                        )}
                        
                        {/* Mobile: Time and servings on own line */}
                        <Group gap="md" hiddenFrom="sm">
                          <Group gap="xs">
                            <IconClock size={16} />
                            <Text size="sm">{formatTime(recipe.totalTimeMinutes)}</Text>
                          </Group>
                          <Group gap="xs">
                            <IconUsers size={16} />
                            <Text size="sm">{recipe.servings} {recipe.servings === 1 ? 'serving' : 'servings'}</Text>
                          </Group>
                        </Group>

                        {/* Tablet+: Time, servings, and functional badges on same line */}
                        <Group gap="xs" visibleFrom="sm">
                          <Group gap="xs">
                            <IconClock size={16} />
                            <Text size="sm">{formatTime(recipe.totalTimeMinutes)}</Text>
                          </Group>
                          <Group gap="xs">
                            <IconUsers size={16} />
                            <Text size="sm">{recipe.servings} {recipe.servings === 1 ? 'serving' : 'servings'}</Text>
                          </Group>
                          {recipe.difficulty && (
                            <Badge
                              size="sm"
                              variant="light"
                              color={recipe.difficulty === 'easy' ? 'green' : recipe.difficulty === 'medium' ? 'yellow' : 'red'}
                            >
                              {recipe.difficulty}
                            </Badge>
                          )}
                          {recipe.usageCount && recipe.usageCount > 0 && (
                            <Badge
                              size="sm"
                              variant="light"
                              color="blue"
                            >
                              Used {recipe.usageCount} time{recipe.usageCount !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          <RecipeProvenanceBadges recipe={recipe} hideTopPick />
                        </Group>

                        {/* Mobile: All badges on single wrapping line */}
                        <Group gap="xs" hiddenFrom="sm">
                          {recipe.difficulty && (
                            <Badge 
                              size="sm" 
                              variant="light"
                              color={recipe.difficulty === 'easy' ? 'green' : recipe.difficulty === 'medium' ? 'yellow' : 'red'}
                            >
                              {recipe.difficulty}
                            </Badge>
                          )}
                          {recipe.usageCount && recipe.usageCount > 0 && (
                            <Badge 
                              size="sm" 
                              variant="light"
                              color="blue"
                            >
                              Used {recipe.usageCount} time{recipe.usageCount !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          <RecipeProvenanceBadges recipe={recipe} hideTopPick />
                          {recipe.cuisine && (
                            <CuisineBadge cuisine={recipe.cuisine} />
                          )}
                          {(() => {
                            const allBadges = getRecipeBadges(recipe, badgeViewer);
                            const { visible, hiddenRestrictions } = splitBadgesForDisplay(allBadges, 1);

                            return (
                              <>
                                {visible.map((badge) => (
                                  <Badge key={`${badge.type}-${badge.text}`} size="sm" variant="outline">
                                    {badge.text}
                                  </Badge>
                                ))}
                                <MoreRestrictionsMenu restrictions={hiddenRestrictions} />
                              </>
                            );
                          })()}
                        </Group>
                        
                        {/* Tablet+: Descriptive badges on second line */}
                        <Group gap="xs" visibleFrom="sm">
                          {recipe.cuisine && (
                            <CuisineBadge cuisine={recipe.cuisine} />
                          )}
                          {(() => {
                            const allBadges = getRecipeBadges(recipe, badgeViewer);
                            const { visible, hiddenRestrictions } = splitBadgesForDisplay(allBadges, 3);

                            return (
                              <>
                                {visible.map((badge) => (
                                  <Badge key={`${badge.type}-${badge.text}`} size="sm" variant="outline">
                                    {badge.text}
                                  </Badge>
                                ))}
                                <MoreRestrictionsMenu restrictions={hiddenRestrictions} />
                              </>
                            );
                          })()}
                        </Group>
                      </Stack>
                    </Flex>
                  </Card>
                ))}
              </SimpleGrid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Group justify="center" mt="xl" mb="md">
                  <Pagination
                    total={totalPages}
                    value={currentPage}
                    onChange={setCurrentPage}
                    size="md"
                    radius="md"
                    color="orange"
                    siblings={0}
                  />
                </Group>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </MobileOptimized>
  );
};
